import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, Badge, Btn, Input, Textarea, Select, Field, Modal } from "@/components/ui-kit";
import QRLabel from "@/components/QRLabel";
import { JOB_STATUSES, PRIORITIES, REPAIR_TYPES, findMeta, fmtDate, fmtMoney, warrantyStatus } from "@/lib/constants";
import { WarrantyAlert } from "@/pages/Inward";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, QrCode, CheckCircle2, ShieldCheck, Wrench, PackageSearch,
  FileText, Cpu, ClipboardList, PlusCircle, Ban, Camera,
} from "lucide-react";
import MediaUploader from "@/components/MediaUploader";

const JOURNEY = [
  { key: "inspection", label: "Visual Inspection" },
  { key: "power", label: "Power Rail Check" },
  { key: "diagnosis", label: "Signal Diagnostics" },
  { key: "repair", label: "Component Repair" },
  { key: "calibration", label: "Calibration" },
  { key: "qa", label: "QA Testing" },
];

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [job, setJob] = useState(null);
  const [qr, setQr] = useState(false);
  const [mediaStage, setMediaStage] = useState("");
  const [diagOpen, setDiagOpen] = useState(false);
  const [partOpen, setPartOpen] = useState(false);
  const [inventory, setInventory] = useState([]);

  const load = useCallback(() => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => toast.error("Job not found"));
  }, [id]);
  useEffect(() => { load(); api.get("/inventory").then((r) => setInventory(r.data)); }, [load]);

  if (!job) return <div className="text-sm text-muted-foreground">Loading workstation…</div>;

  const st = findMeta(JOB_STATUSES, job.status);
  const pr = findMeta(PRIORITIES, job.priority);
  const canQA = can("job.qa");
  const canEdit = can("job.edit");

  const setStatus = async (status) => {
    try { await api.put(`/jobs/${id}`, { status }); toast.success(`Status → ${findMeta(JOB_STATUSES, status).label}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const qaPass = async () => {
    try { await api.post(`/jobs/${id}/qa`); toast.success("QA sign-off recorded"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="job-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={15} /> Back</button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">{job.equipment_name}</h1>
            <Badge className={st.cls}>{st.label}</Badge>
            <span className={`font-mono text-xs font-bold ${pr.cls}`}>{pr.label.toUpperCase()}</span>
          </div>
          <div className="font-mono text-xs text-muted-foreground">
            {job.job_number} · SN {job.serial_number} · {job.brand} {job.model_number}
            {job.batch_number && <> · <span className="text-primary" data-testid="job-batch-ref">{job.batch_number} ({job.batch_index}/{job.batch_quantity})</span></>}
          </div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            <Badge className={findMeta(REPAIR_TYPES, job.repair_type || "new_repair").cls} data-testid="job-repair-type">{findMeta(REPAIR_TYPES, job.repair_type || "new_repair").label}</Badge>
            {job.repair_type === "warranty_repair" && <span className="font-mono text-[11px] text-muted-foreground">Ref {job.warranty_ref || "—"}{warrantyStatus(job) && <> · Warranty {warrantyStatus(job).source === "outward" ? `from ${warrantyStatus(job).from}` : `OEM ${warrantyStatus(job).months}`} → {fmtDate(warrantyStatus(job).expiry)}</>}</span>}
            <WarrantyAlert job={job} />
            {job.outward?.warranty_provided && <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30" data-testid="job-outward-warranty">Warranty issued: {job.outward.warranty_period || "yes"} · {fmtDate(job.outward.dispatch_date)}{job.outward.warranty_until ? ` → ${fmtDate(job.outward.warranty_until)}` : ""}</Badge>}
          </div>
        </div>
        <div className="flex gap-2">
          <Btn variant="outline" onClick={() => setQr(true)} data-testid="job-qr-btn"><QrCode size={15} /> Label</Btn>
          {canQA && job.status !== "dispatched" && (
            <Btn variant="success" onClick={qaPass} data-testid="qa-signoff-btn"><ShieldCheck size={15} /> QA Pass</Btn>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Left: guided journey + diagnostics */}
        <div className="space-y-4 lg:col-span-2">
          <Card className="p-4">
            <h3 className="mb-3 flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-wide"><Wrench size={15} /> Guided Repair Journey</h3>
            <div className="flex flex-wrap gap-2">
              {JOURNEY.map((s, i) => {
                const done = job.diagnostics.some((d) => d.step === s.key);
                return (
                  <div key={s.key} className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium ${done ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-500" : "border-border text-muted-foreground"}`}>
                    <span className="font-mono text-[10px]">{String(i + 1).padStart(2, "0")}</span>
                    {done && <CheckCircle2 size={13} />} {s.label}
                  </div>
                );
              })}
            </div>
            <div className="mt-3 flex gap-2">
              {canEdit && <Btn size="sm" onClick={() => setDiagOpen(true)} data-testid="add-diagnostic-btn"><PlusCircle size={14} /> Log Diagnostic</Btn>}
              {canEdit && <Btn size="sm" variant="outline" onClick={() => setPartOpen(true)} data-testid="request-part-btn"><PackageSearch size={14} /> Request Part</Btn>}
              <Select className="w-44" value={job.status} onChange={(e) => setStatus(e.target.value)} data-testid="job-status-select" disabled={!canEdit}>
                {JOB_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </Select>
            </div>
          </Card>

          <Card className="p-4">
            <h3 className="mb-3 flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-wide"><ClipboardList size={15} /> Diagnostic Log</h3>
            {job.diagnostics.length === 0 ? <p className="text-sm text-muted-foreground">No diagnostics logged yet.</p> : (
              <div className="space-y-2">
                {job.diagnostics.map((d) => (
                  <div key={d.id} className="rounded-md border border-border p-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs uppercase text-primary">{d.step}</span>
                      <span className="font-mono text-[10px] text-muted-foreground">{d.technician_name} · {fmtDate(d.created_at)}</span>
                    </div>
                    {d.symptom_reported && <p className="mt-1"><b>Symptom:</b> {d.symptom_reported}</p>}
                    {d.observed_fault && <p><b>Observed:</b> {d.observed_fault}</p>}
                    {d.root_cause_analysis && <p><b>Root cause:</b> {d.root_cause_analysis}</p>}
                    {d.action_taken && <p><b>Action:</b> {d.action_taken}</p>}
                    {d.is_ber && <Badge className="mt-1 bg-red-500/20 text-red-500 border-red-500/40"><Ban size={11} /> Beyond Economical Repair</Badge>}
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h3 className="flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-wide"><Camera size={15} /> Photos & Videos</h3>
              <div className="inline-flex gap-1 rounded-md border border-border p-1">
                {[["", "All"], ["inward", "Inward"], ["repair", "Repair"], ["outward", "Outward"]].map(([v, l]) => (
                  <button key={v} type="button" data-testid={`media-stage-${v || "all"}`} onClick={() => setMediaStage(v)}
                    className={`rounded px-2.5 py-1 text-[11px] font-bold uppercase ${mediaStage === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}>{l}</button>
                ))}
              </div>
            </div>
            <MediaUploader entityType="job" entityId={job.id} stage={mediaStage || undefined} />
            <p className="mt-2 text-[11px] text-muted-foreground">Uploads here are tagged <b>{mediaStage || "repair"}</b>. On a phone, Photo / Video opens the camera directly.</p>
          </Card>
        </div>

        {/* Right: details + parts */}
        <div className="space-y-4">
          <Card className="p-4">
            <h3 className="mb-2 font-heading text-sm font-bold uppercase tracking-wide">Equipment Details</h3>
            <dl className="space-y-1.5 text-sm">
              <Row k="Category" v={job.equipment_category || "—"} />
              <Row k="RMA" v={job.tracking_rma || "—"} />
              <Row k="Client Email" v={job.client_email || "—"} />
              {job.status_token && (
                <button type="button" data-testid="copy-status-link" className="mt-1 text-xs font-semibold text-primary hover:underline"
                  onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/status/${job.status_token}`); toast.success("Client status link copied"); }}>
                  Copy client status link
                </button>
              )}
              <Row k="Accessories" v={job.included_accessories || "—"} />
              <Row k="Condition" v={job.physical_condition || "—"} />
              <Row k="Received" v={fmtDate(job.received_at)} />
              {job.qa_approved_by && <Row k="QA By" v={job.qa_approved_by} />}
            </dl>
            {job.condition_checklist && Object.keys(job.condition_checklist).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {Object.entries(job.condition_checklist).map(([k, v]) => (
                  <span key={k} className={`rounded px-1.5 py-0.5 font-mono text-[10px] ${v ? "bg-amber-500/15 text-amber-500" : "bg-muted text-muted-foreground"}`}>{k}:{v ? "Y" : "N"}</span>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-4">
            <h3 className="mb-2 flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-wide"><Cpu size={15} /> Part Requests</h3>
            {job.part_requests.length === 0 ? <p className="text-sm text-muted-foreground">No part requests.</p> : (
              <div className="space-y-1.5">
                {job.part_requests.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-md border border-border px-2.5 py-1.5 text-sm">
                    <span>{p.part_name} <span className="font-mono text-xs text-muted-foreground">×{p.quantity}</span></span>
                    <Badge className="bg-amber-500/15 text-amber-500 border-amber-500/30">{p.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {job.outward && (
            <Card className="p-4">
              <h3 className="mb-2 font-heading text-sm font-bold uppercase tracking-wide">Dispatch Record</h3>
              <dl className="space-y-1.5 text-sm">
                <Row k="Outcome" v={job.outward.repair_outcome} />
                <Row k="Dispatched" v={fmtDate(job.outward.dispatch_date)} />
                <Row k="Courier" v={job.outward.courier_details || "—"} />
                <Row k="Signoff" v={job.outward.client_signoff_name || "—"} />
              </dl>
            </Card>
          )}
        </div>
      </div>

      {diagOpen && <DiagForm jobId={id} onClose={() => setDiagOpen(false)} onSaved={() => { setDiagOpen(false); load(); }} />}
      {partOpen && <PartForm jobId={id} inventory={inventory} onClose={() => setPartOpen(false)} onSaved={() => { setPartOpen(false); load(); }} />}
      <QRLabel open={qr} onClose={() => setQr(false)} title={job.equipment_name} code={job.job_number}
        badge={findMeta(REPAIR_TYPES, job.repair_type || "new_repair").label}
        lines={[["SERIAL", job.serial_number], ["MODEL", job.model_number || "—"],
          job.batch_number ? ["BATCH", `${job.batch_number} · ${job.batch_index}/${job.batch_quantity}`] : ["RMA", job.tracking_rma || "—"], ["PRIORITY", job.priority]]} />
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between gap-2"><dt className="font-mono text-[11px] uppercase text-muted-foreground">{k}</dt><dd className="text-right">{v}</dd></div>;
}

function DiagForm({ jobId, onClose, onSaved }) {
  const [f, setF] = useState({ step: "diagnosis", symptom_reported: "", observed_fault: "", root_cause_analysis: "", action_taken: "", is_ber: false });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try { await api.post("/diagnostics", { job_id: jobId, ...f }); toast.success("Diagnostic logged"); onSaved(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open onClose={onClose} title="Log Diagnostic Step">
      <div className="space-y-3">
        <Field label="Journey Step">
          <Select value={f.step} onChange={set("step")} data-testid="diag-step">
            {JOURNEY.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </Select>
        </Field>
        <Field label="Symptom Reported"><Textarea rows={2} value={f.symptom_reported} onChange={set("symptom_reported")} /></Field>
        <Field label="Observed Fault"><Textarea rows={2} value={f.observed_fault} onChange={set("observed_fault")} /></Field>
        <Field label="Root Cause Analysis"><Textarea rows={2} value={f.root_cause_analysis} onChange={set("root_cause_analysis")} /></Field>
        <Field label="Action Taken"><Textarea rows={2} value={f.action_taken} onChange={set("action_taken")} /></Field>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" data-testid="diag-ber" checked={f.is_ber} onChange={(e) => setF({ ...f, is_ber: e.target.checked })} />
          Flag as Beyond Economical Repair (BER)
        </label>
        <div className="flex justify-end gap-2"><Btn variant="outline" onClick={onClose}>Cancel</Btn><Btn onClick={save} data-testid="diag-save-btn">Save</Btn></div>
      </div>
    </Modal>
  );
}

function PartForm({ jobId, inventory, onClose, onSaved }) {
  const [f, setF] = useState({ item_id: "", part_name: "", quantity: 1, notes: "" });
  const save = async () => {
    if (!f.part_name) { toast.error("Part name required"); return; }
    try { await api.post("/part-requests", { job_id: jobId, ...f, item_id: f.item_id || null, quantity: Number(f.quantity) }); toast.success("Part requested"); onSaved(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open onClose={onClose} title="Request Part">
      <div className="space-y-3">
        <Field label="Search local inventory">
          <Select value={f.item_id} data-testid="part-inventory-select" onChange={(e) => {
            const it = inventory.find((i) => i.id === e.target.value);
            setF({ ...f, item_id: e.target.value, part_name: it ? it.name : f.part_name });
          }}>
            <option value="">— External requisition —</option>
            {inventory.map((i) => <option key={i.id} value={i.id}>{i.name} ({i.quantity_on_hand} in {i.bin_location})</option>)}
          </Select>
        </Field>
        <Field label="Part Name" required><Input data-testid="part-name" value={f.part_name} onChange={(e) => setF({ ...f, part_name: e.target.value })} /></Field>
        <Field label="Quantity"><Input type="number" min={1} value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} /></Field>
        <Field label="Notes"><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
        <div className="flex justify-end gap-2"><Btn variant="outline" onClick={onClose}>Cancel</Btn><Btn onClick={save} data-testid="part-save-btn">Request</Btn></div>
      </div>
    </Modal>
  );
}
